/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

const SearchResultContainerView: React.FC<ISearchResultContainerViewProps> = props => {
    const {
        SearchResultContainer,
        products,
        pagination,
        ProductsContainer,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        errorMessage,
        FeatureSearchContainer,
        similarLookProduct
    } = props;
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;

    React.useEffect(() => {
        setTimeout(async () => {
            const paginationContainer = document.querySelector('.ms-search-result-container__pagination .msc-pagination');
    
            if (!paginationContainer) {
                // Your existing non-pagination container code
                const productContainer = document.querySelector('.ms-search-result-container__Products .list-unstyled');
        
                if (!productContainer) {
                    console.log('Product container not found');
                    window.location.reload();
                    return;
                }
    
                // Get sort direction from URL parameters
                const urlParams = new URLSearchParams(window.location.search);
                const sortingParam = urlParams.get('sorting');
                
                let sortDirection: 'asc' | 'desc' = 'asc'; // Default to ascending
                
                if (sortingParam) {
                    try {
                        const sortingData = JSON.parse(decodeURIComponent(sortingParam));
                        if (Array.isArray(sortingData) && sortingData.length > 0) {
                            const sortConfig = sortingData[0];
                            // Check if we're sorting by NAME and get the direction
                            if (sortConfig.ColumnName === 'NAME') {
                                sortDirection = sortConfig.IsDescending ? 'desc' : 'asc';
                            }
                        }
                    } catch (error) {
                        console.error('Error parsing sorting parameter:', error);
                    }
                }
    
                // Check if current page is sorting by NAME
                const isSortingByName = window.location.search.includes('ColumnName%22%3A%22NAME');
                
                // Check if previous page was sorting by NAME using sessionStorage
                const wasPreviousPageSortingByName = sessionStorage.getItem('wasSortingByName') === 'true';
                
                if (isSortingByName) {
                    
                    sessionStorage.setItem('wasSortingByName', 'true');
                    
                    const productElements = Array.from(productContainer.children) as HTMLElement[];
        
                    const sortedElements = productElements.sort((a, b) => {
                        // Get product name from the title element
                        const titleA = a.querySelector('.msc-product__title')?.textContent?.trim() || '';
                        const titleB = b.querySelector('.msc-product__title')?.textContent?.trim() || '';
                        const comparison = titleA.localeCompare(titleB);
                        return sortDirection === 'desc' ? -comparison : comparison;
                    });
                    
                    // Clear and reappend in sorted order
                    while (productContainer.firstChild) {
                        productContainer.removeChild(productContainer.firstChild);
                    }
                    sortedElements.forEach(el => productContainer.appendChild(el));
                    
                } else {
                    // If we're not sorting by NAME now, but were on the previous page
                    if (wasPreviousPageSortingByName) {
                        // Clear the flag to prevent infinite refresh loop
                        sessionStorage.removeItem('wasSortingByName');
                        
                        // Refresh the page to get the server-side sorted results
                        console.log('Refreshing page to get server-side sorting');
                        window.location.reload();
                        return; // Early return since we're refreshing
                    }
                }
            } else {
                // Get current skip parameter from URL
                const urlParams = new URLSearchParams(window.location.search);
                const currentSkip = parseInt(urlParams.get('skip') || '0');
    
                // Check if current page is sorting by NAME
                const isSortingByName = window.location.search.includes('ColumnName%22%3A%22NAME');
                
                // Check if previous page was sorting by NAME using sessionStorage
                const wasPreviousPageSortingByName = sessionStorage.getItem('wasSortingByName') === 'true';
    
                // If we're not sorting by NAME now, but were on the previous page
                if (!isSortingByName && wasPreviousPageSortingByName) {
                    // Clear the flag to prevent infinite refresh loop
                    sessionStorage.removeItem('wasSortingByName');
                    
                    // Refresh the page to get the server-side sorted results
                    console.log('Refreshing page to get server-side sorting');
                    window.location.reload();
                    return; // Early return since we're refreshing
                }
    
                if (isSortingByName) {
    
                    sessionStorage.setItem('wasSortingByName', 'true');
    
                    const isProductsFetching = sessionStorage.getItem('isProductsFetching') === 'true';
                    if (isProductsFetching) {
                        return;
                    }
        
                    // Set flag to indicate products fetching is in progress with current page
                    sessionStorage.setItem('isProductsFetching', 'true');
        
                    // Get all pagination items to determine the total number of pages
                    const paginationItems = Array.from(paginationContainer.querySelectorAll('li.msc-page-item'));
                    const pageNumbers = paginationItems
                        .map(item => {
                            const link = item.querySelector('a, span');
                            const text = link?.textContent?.trim();
                            return text && text !== '...' && text !== 'Previous' && text !== 'Next' ? parseInt(text) : null;
                        })
                        .filter(page => page !== null && !isNaN(page));
        
                    const totalPages = Math.max(...pageNumbers as number[]);
                    const productsPerPage = 30;
        
                    const allProducts: HTMLElement[] = [];
        
                    // Function to extract products from HTML
                    const extractProductsFromHTML = (html: string): HTMLElement[] => {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(html, 'text/html');
                        const productContainer = doc.querySelector('.ms-search-result-container__Products .list-unstyled');
                        return productContainer ? Array.from(productContainer.children) as HTMLElement[] : [];
                    };
        
                    // Function to fetch and process a single page
                    const fetchPage = async (skipValue: number): Promise<HTMLElement[]> => {
                        try {
                            const url = new URL(window.location.href);
                            if (skipValue > 0) {
                                url.searchParams.set('skip', skipValue.toString());
                            } else {
                                url.searchParams.delete('skip');
                            }
                            
                            const response = await fetch(url.toString());
                            const html = await response.text();
                            return extractProductsFromHTML(html);
                        } catch (error) {
                            console.error('Error fetching page with skip=', skipValue, error);
                            return [];
                        }
                    };
        
                    // Process all pagination pages
                    const fetchAllPages = async () => {
                        try {
                            // Get current page products first
                            const currentProductContainer = document.querySelector('.ms-search-result-container__Products .list-unstyled');
                            if (currentProductContainer) {
                                const currentProducts = Array.from(currentProductContainer.children) as HTMLElement[];
                                allProducts.push(...currentProducts);
                                // Clear current products
                                while (currentProductContainer.firstChild) {
                                    currentProductContainer.removeChild(currentProductContainer.firstChild);
                                }
                                
                                //ADD A SPINNNIG WHEEL
                                const spinner = document.createElement('div');
                                spinner.style.cssText = `
                                    display: flex;
                                    justify-content: center;
                                    align-items: center;
                                    min-height: 300px;
                                    width: 100%;
                                `;

                                const spinnerElement = document.createElement('div');
                                spinnerElement.style.cssText = `
                                    width: 40px;
                                    height: 40px;
                                    border: 4px solid #f3f3f3;
                                    border-top: 4px solid #666;
                                    border-radius: 50%;
                                    animation: spin 1s linear infinite;
                                `;

                                // Add the CSS animation to the document head
                                const style = document.createElement('style');
                                style.textContent = `
                                    @keyframes spin {
                                        0% { transform: rotate(0deg); }
                                        100% { transform: rotate(360deg); }
                                    }
                                `;
                                document.head.appendChild(style);

                                spinner.appendChild(spinnerElement);
                                const parentContainer = document.querySelector('.ms-search-result-container__Products');
                                if (parentContainer)
                                {
                                    parentContainer.appendChild(spinner);    
                                } 
                            }
        
                            // PARALLEL FETCHING: Create all fetch promises at once
                            const fetchPromises: Promise<HTMLElement[]>[] = [];
                            
                            for (let page = 1; page <= totalPages; page++) {
                                const skipValue = (page - 1) * productsPerPage;
                                
                                // Skip current page as we already have its products
                                if (skipValue === currentSkip) continue;
                                
                                fetchPromises.push(fetchPage(skipValue));
                            }
        
                            // Wait for ALL pages to fetch in parallel
                            console.log(`Fetching pages in parallel...`);
                            const allPageResults = await Promise.all(fetchPromises);
                            
                            // Combine all results
                            allPageResults.forEach(pageProducts => {
                                allProducts.push(...pageProducts);
                            });
        
                            console.log('PARALLEL FETCHING COMPLETED!');          
                            
                            // Get sort direction from URL parameters
                            const sortingParam = urlParams.get('sorting');
                            let sortDirection: 'asc' | 'desc' = 'asc';
                            
                            if (sortingParam) {
                                try {
                                    const sortingData = JSON.parse(decodeURIComponent(sortingParam));
                                    if (Array.isArray(sortingData) && sortingData.length > 0) {
                                        const sortConfig = sortingData[0];
                                        if (sortConfig.ColumnName === 'NAME') {
                                            sortDirection = sortConfig.IsDescending ? 'desc' : 'asc';
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error parsing sorting parameter:', error);
                                }
                            }
    
                            // Sort all fetched products
                            const sortedProducts = allProducts.sort((a, b) => {
                                const titleA = a.querySelector('.msc-product__title')?.textContent?.trim() || '';
                                const titleB = b.querySelector('.msc-product__title')?.textContent?.trim() || '';
                                const comparison = titleA.localeCompare(titleB);
                                return sortDirection === 'desc' ? -comparison : comparison;
                            });
    
                            // Skip first N products and take first 30 for current page
                            const productsToDisplay = sortedProducts.slice(currentSkip, currentSkip + 30);
    
                            // Update the DOM with the sorted products
                            if (currentProductContainer) {
                                
                                 //REMOVE THE SPINNNIG WHEEL 
                                const parentContainer = document.querySelector('.ms-search-result-container__Products');
                                if (parentContainer) {
                                    const spinner = parentContainer.querySelector('div[style*="display: flex"][style*="justify-content: center"][style*="align-items: center"]');
                                    if (spinner) {
                                        parentContainer.removeChild(spinner);
                                    }   
                                }

                                // Add sorted products from fetch
                                productsToDisplay.forEach(product => {
                                    currentProductContainer.appendChild(product);
                                });
                            }
    
                            console.log('Products sorted and displayed');
                            
                        } catch (error) {
                            console.error('Error during parallel fetching:', error);
                        } finally {
                            // Always clear the products fetching flag
                            sessionStorage.removeItem('isProductsFetching');
                        }
                    };
        
                    await fetchAllPages();
                }
            }
        }, 100); // Small delay to ensure rendering
    });
    
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                {renderTitle(TitleViewProps)}
                {choiceSummary}
                {modalToggle}
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                <Node {...ProductsContainer}>
                    {errorMessage}
                    {products}
                </Node>
                {pagination}
            </Module>
        );
    }
    return (
        <Module {...SearchResultContainer}>
            {categoryHierarchy && <Node {...CategoryNavContainer}>{renderCategoryHierarchy(categoryHierarchy)}</Node>}
            <Node {...RefineAndProductSectionContainer}>
                {refineMenu && renderRefiner(refineMenu)}
                <Node {...ProductSectionContainer}>
                    <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                    <div className='ms-search-result-wrapper-title-choice-summary'>
                        {TitleViewProps && renderTitle(TitleViewProps)}
                        {choiceSummary}
                    </div>
                    <div className='ms-search-result-wrapper-sort-by-category'>
                        {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                    </div>
                    <Node {...ProductsContainer}>
                        {errorMessage}
                        {products}
                    </Node>
                    {pagination}
                </Node>
            </Node>
        </Module>
    );
};

const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefiner(refineMenu));
    }
    return null;
};

const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <h1>
                    {title.titlePrefix}
                    {title.titleText}({title.titleCount})
                </h1>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
